import { ReactNode, Suspense, lazy, useCallback, useEffect } from 'react'
import {
  Route,
  Routes,
  Outlet,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { Toaster } from 'react-hot-toast'

import { useAuth } from 'ctx/AuthContext'
import { useUI } from 'ctx/UIContext'

import LoadingScreen from 'components/layout/LoadingScreen'
import 'react-tooltip/dist/react-tooltip.css'
import { useUser } from 'store/RootStore'
import Guides from 'pages/Common/Guides'
import BulkSubmissions from 'pages/Dashboard/BulkSubmission'

const ForgotPassword = lazy(() => import('pages/Auth/ForgotPassword'))
const ResetPassword = lazy(() => import('pages/Auth/ResetPassword'))
const Login = lazy(() => import('pages/Auth/Login'))
const Signup = lazy(() => import('pages/Auth/Signup'))
const ConfirmSignup = lazy(() => import('pages/Auth/ConfirmSignup'))
const MainDashboard = lazy(() => import('pages/Dashboard'))
const AddSubmission = lazy(() => import('pages/Dashboard/AddSubmission'))
const EditSubmission = lazy(() => import('pages/Dashboard/EditSubmission'))
const MySubmissions = lazy(() => import('pages/Dashboard/MySubmissions'))
const Analytics = lazy(() => import('pages/Dashboard/Analytics'))
const AccountSettings = lazy(() => import('pages/Common/AccountSettings'))
const Unsubscribe = lazy(() => import('pages/Common/Unsubscribe'))

function App() {
  const ui = useUI()
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { getUser } = useUser()

  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/my-submissions')
    }
  }, [location])

  // useEffect(() => {
  //   localStorage.clear()
  // }, [])

  const [searchParams] = useSearchParams()

  const affiliate_id = searchParams.get('affiliate')
  const affiliate_type = searchParams.get('type')

  // console.log(affiliate_id, affiliate_type)
  // ?affiliate=NCDLVKL9AU&affiliateType=newType

  useEffect(() => {
    if (affiliate_id) {
      localStorage.setItem('affiliate', affiliate_id)
    }
    if (affiliate_type) {
      localStorage.setItem('affiliate_type', affiliate_type)
    }
  }, [])

  const PrivateRoute = useCallback(
    () => (auth?.user ? <Outlet /> : <Navigate to='/login' />),
    [auth?.user]
  )

  return (
    <>
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route
          path='/signup'
          element={
            <SuspensedView>
              <Signup />
            </SuspensedView>
          }
        />
        <Route
          path='/forgot-password'
          element={
            <SuspensedView>
              <ForgotPassword />
            </SuspensedView>
          }
        />
        <Route
          path='/reset-password'
          element={
            <SuspensedView>
              <ResetPassword />
            </SuspensedView>
          }
        />
        <Route
          path='/confirm-signup'
          element={
            <SuspensedView>
              <ConfirmSignup />
            </SuspensedView>
          }
        />
        <Route
          path='/unsubscribe-confirm'
          element={
            <SuspensedView>
              <Unsubscribe />
            </SuspensedView>
          }
        />
        <Route path='/' element={<PrivateRoute />}>
          <Route
            path='/'
            element={
              <SuspensedView>
                <MainDashboard />
              </SuspensedView>
            }
          >
            <Route
              path='add-submission'
              element={
                <SuspensedView>
                  <AddSubmission />
                </SuspensedView>
              }
            />
            <Route
              path='submission/:type/:id'
              element={
                <SuspensedView>
                  <EditSubmission />
                </SuspensedView>
              }
            />
            <Route
              path='my-submissions'
              element={
                <SuspensedView>
                  <MySubmissions />
                </SuspensedView>
              }
            />
            <Route
              path='bulk-submissions'
              element={
                <SuspensedView>
                  <BulkSubmissions />
                </SuspensedView>
              }
            />
            <Route
              path='analytics'
              element={
                <SuspensedView>
                  <Analytics />
                </SuspensedView>
              }
            />

            <Route
              path='account-settings'
              element={
                <SuspensedView>
                  <AccountSettings />
                </SuspensedView>
              }
            />
            <Route
              path='guides'
              element={
                <SuspensedView>
                  <Guides />
                </SuspensedView>
              }
            />
          </Route>
        </Route>
      </Routes>
      <Toaster
        toastOptions={{
          duration: 6000,
          position: 'bottom-center',
          // Styling
          style: {
            borderRadius: '10px',
            color: '#eee',
          },
          className: 'rounded-full !bg-primary-purple',
        }}
      />
      {ui.isLoading ? <LoadingScreen isLoading /> : null}
      {/* <LoadingScreen isLoading /> */}
    </>
  )
}

const SuspensedView = ({ children }: { children: ReactNode }) => {
  return <Suspense fallback={<LoadingScreen isLoading />}>{children}</Suspense>
}

export default App
